/**
 * The shape of an address and the rules it must satisfy.
 *
 * Kept apart from the `AddressFields` component that renders it: the shape and
 * its validation are also needed where there is nothing to render — building a
 * blank form, assembling a payload, checking a step before advancing — and
 * those callers should not have to pull in React and the store to ask whether
 * a commune is missing.
 */

/**
 * @typedef {object} AddressValue
 * @property {string} street
 * @property {string} streetNumber
 * @property {string} region
 * @property {string} commune
 * @property {string} settlementId - Row of the catalog the colonia was picked
 *   from. Empty on an address loaded from the backend, which stores the name.
 * @property {string} settlement
 * @property {string} postalCode
 */

/** The shape a consumer should spread into its own blank form. */
export const BLANK_ADDRESS = {
  street: '',
  streetNumber: '',
  region: '',
  commune: '',
  settlementId: '',
  settlement: '',
  postalCode: '',
};

/**
 * Validates an address the same way wherever it is entered.
 *
 * Requires the settlement by **name**, not by catalog id: the name is what
 * travels to the backend. On a new address the only way to fill it is by
 * picking a row, so this is equivalent to requiring the id; on a stored address
 * it lets an already-saved colonia through instead of forcing the operator to
 * pick it again just to change the street number.
 *
 * The postal code needs no check of its own — it comes from the same row as the
 * settlement, so one cannot be present without the other.
 *
 * @param {AddressValue} value
 * @param {{isMexico: boolean}} options
 * @returns {Object.<string, string>} Errors by field name; empty when valid.
 */
export const validateAddress = (value, { isMexico }) => {
  const errors = {};

  if (!String(value.street || '').trim()) errors.street = 'Campo obligatorio';
  if (!String(value.streetNumber || '').trim()) errors.streetNumber = 'Campo obligatorio';
  if (!value.region) errors.region = 'Campo obligatorio';
  if (!value.commune) errors.commune = 'Campo obligatorio';
  if (isMexico && !value.settlement) errors.settlementId = 'Campo obligatorio';

  return errors;
};
